import { utils } from "@project-serum/anchor";
import { getProgramAddress } from "@saberhq/solana-contrib";
import { Token, TokenAmount } from "@saberhq/token-utils";
import { PublicKey } from "@solana/web3.js";
import JSBI from "jsbi";
import invariant from "tiny-invariant";
import { SABER_ADDRESSES } from "../../constants";
/**
 * Wrapped token with altered decimals.
 *
 * To reload this token after initializing it, construct a "WrappedTokenActions"
 * and call `reload`.
 */
export class LazyWrappedToken {
    constructor(underlying, wrapper, mintAccount, decimals) {
        this.underlying = underlying;
        this.wrapper = wrapper;
        this.mintAccount = mintAccount;
        this.decimals = decimals;
    }
    get name() {
        const decimalsSuffix = this.decimals === this.underlying.decimals
            ? ``
            : ` (${this.decimals} decimals)`;
        return `Saber Wrapped ${this.underlying.name}${decimalsSuffix}`;
    }
    get symbol() {
        const decimalsSuffix = this.decimals === this.underlying.decimals ? `` : `-${this.decimals}`;
        return `s${this.underlying.symbol}${decimalsSuffix}`;
    }
    get chainId() {
        return this.underlying.chainId;
    }
    get network() {
        return this.underlying.network;
    }
    get icon() {
        return this.underlying.icon;
    }
    get address() {
        var _a, _b;
        return ((_b = (_a = this.mintAccount) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : null);
    }
    get multiplier() {
        return 10 ** (this.decimals - this.underlying.decimals);
    }
    equals(other) {
        if (other instanceof WrappedToken) {
            return other.wrapper.equals(this.wrapper);
        }
        if (this.address) {
            return this.address === other.address;
        }
        return false;
    }
    get token() {
        const address = this.address;
        if (!address) {
            return null;
        }
        return new Token({
            name: this.name,
            address,
            decimals: this.decimals,
            symbol: this.symbol,
            chainId: this.chainId,
        });
    }
    /**
     * Loads a wrapped token.
     */
    static async load(program, underlying, decimals) {
        const [wrapperAddress] = await WrappedToken.getAddressAndNonce(program.programId, underlying.mintAccount, decimals);
        try {
            const data = await program.account.wrappedToken.fetch(wrapperAddress);
            return new WrappedToken(underlying, wrapperAddress, data.wrapperMint, decimals);
        }
        catch (e) {
            if (e instanceof Error && e.message.includes("Account does not exist")) {
                return new LazyWrappedToken(underlying, wrapperAddress, null, decimals);
            }
            throw e;
        }
    }
    /**
     * Gets the address and nonce of a wrapped token.
     * @param programID
     * @param underlyingMint
     * @param decimals
     * @returns
     */
    static async getAddressAndNonce(programID, underlyingMint, decimals) {
        return await PublicKey.findProgramAddress([
            Buffer.from(utils.bytes.utf8.encode("anchor")),
            underlyingMint.toBytes(),
            Buffer.from([decimals]),
        ], programID);
    }
    /**
     * Gets the address of a wrapped token.
     * @param programID
     * @param underlyingMint
     * @param decimals
     * @returns
     */
    static getAddressSync(underlyingMint, decimals) {
        return getProgramAddress([
            utils.bytes.utf8.encode("anchor"),
            underlyingMint.toBytes(),
            Buffer.from([decimals]),
        ], SABER_ADDRESSES.AddDecimals);
    }
}
/**
 * Wrapped token that is already initialized.
 */
export class WrappedToken extends LazyWrappedToken {
    calculateDepositOutputAmount(inputAmount) {
        invariant(inputAmount.token.equals(this.underlying), "input deposit mismatch");
        return new TokenAmount(this.token, JSBI.multiply(inputAmount.raw, JSBI.BigInt(this.multiplier)));
    }
    calculateWithdrawOutputAmount(inputAmount) {
        invariant(inputAmount.token.equals(this.token), "input withdrawal mismatch");
        return new TokenAmount(this.underlying, JSBI.divide(inputAmount.raw, JSBI.BigInt(this.multiplier)));
    }
    calculateOutputAmount(inputAmount) {
        let outputAmount = null;
        if (this.token.equals(inputAmount.token)) {
            // withdraw, so divide
            outputAmount = this.calculateWithdrawOutputAmount(inputAmount);
        }
        else if (inputAmount.token.equals(this.underlying)) {
            // deposit, so multiply
            outputAmount = this.calculateDepositOutputAmount(inputAmount);
        }
        invariant(outputAmount, `unknown input token: ${inputAmount.token.mintAccount.toString()}`);
        return outputAmount;
    }
}
//# sourceMappingURL=wrappedToken.js.map